import type {
	ApiResponse,
	AuthTokens,
	User,
} from "@employee-management/common";
import axios, { type AxiosError, type AxiosInstance } from "axios";

const API_BASE_URL =
	(import.meta as unknown as { env: { VITE_API_URL: string } }).env
		.VITE_API_URL || "/api";

class AuthService {
	public api: AxiosInstance;

	constructor() {
		this.api = axios.create({
			baseURL: API_BASE_URL,
			headers: {
				"Content-Type": "application/json",
			},
		});

		// Add request interceptor to include auth token
		this.api.interceptors.request.use(
			(config) => {
				const tokens = this.getStoredTokens();
				if (tokens?.accessToken) {
					config.headers.Authorization = `Bearer ${tokens.accessToken}`;
				}
				return config;
			},
			(error) => {
				return Promise.reject(error);
			},
		);

		// Add response interceptor to handle token refresh
		this.api.interceptors.response.use(
			(response) => response,
			async (error) => {
				const originalRequest = error.config;

				if (error.response?.status === 401 && !originalRequest._retry) {
					originalRequest._retry = true;

					try {
						const tokens = this.getStoredTokens();
						if (tokens?.refreshToken) {
							const result = await this.refreshToken(tokens.refreshToken);
							this.storeTokens(result);

							// Retry the original request with new token
							originalRequest.headers.Authorization = `Bearer ${result.accessToken}`;
							return this.api(originalRequest);
						}
					} catch (_refreshError) {
						// Refresh failed, clear tokens and redirect to login
						this.clearTokens();
						window.location.href = "/login";
					}
				}

				return Promise.reject(error);
			},
		);
	}

	private getStoredTokens(): AuthTokens | null {
		try {
			const accessToken = localStorage.getItem("accessToken");
			const refreshToken = localStorage.getItem("refreshToken");

			if (accessToken && refreshToken) {
				return { accessToken, refreshToken };
			}
			return null;
		} catch {
			return null;
		}
	}

	private storeTokens(tokens: AuthTokens): void {
		try {
			localStorage.setItem("accessToken", tokens.accessToken);
			localStorage.setItem("refreshToken", tokens.refreshToken);
		} catch (error) {
			console.error("Failed to store tokens:", error);
		}
	}

	public clearTokens(): void {
		try {
			localStorage.removeItem("accessToken");
			localStorage.removeItem("refreshToken");
		} catch (error) {
			console.error("Failed to clear tokens:", error);
		}
	}

	public async login(
		email: string,
		password: string,
	): Promise<{ user: User; accessToken: string; refreshToken: string }> {
		try {
			const response = await this.api.post("/auth/login", { email, password });

			if (response.data.success) {
				const { user, accessToken, refreshToken } = response.data.data;
				this.storeTokens({ accessToken, refreshToken });
				return { user, accessToken, refreshToken };
			} else {
				throw new Error(response.data.error?.message || "Login failed");
			}
		} catch (error: unknown) {
			if ((error as AxiosError<ApiResponse>).response?.data?.error?.message) {
				throw new Error(
					(error as AxiosError<ApiResponse>).response?.data.error?.message,
				);
			}
			throw new Error("Login failed. Please check your credentials.");
		}
	}

	public async refreshToken(
		refreshToken?: string,
	): Promise<{ user: User; accessToken: string; refreshToken: string }> {
		try {
			const token = refreshToken || this.getStoredTokens()?.refreshToken;
			if (!token) {
				throw new Error("No refresh token available");
			}

			const response = await this.api.post("/auth/refresh", {
				refreshToken: token,
			});

			if (response.data.success) {
				const {
					user,
					accessToken,
					refreshToken: newRefreshToken,
				} = response.data.data;
				this.storeTokens({ accessToken, refreshToken: newRefreshToken });
				return { user, accessToken, refreshToken: newRefreshToken };
			} else {
				throw new Error(response.data.error?.message || "Token refresh failed");
			}
		} catch (error: unknown) {
			if ((error as AxiosError<ApiResponse>).response?.data?.error?.message) {
				throw new Error(
					(error as AxiosError<ApiResponse>).response?.data.error?.message,
				);
			}
			throw new Error("Token refresh failed");
		}
	}

	public async logout(): Promise<void> {
		try {
			const tokens = this.getStoredTokens();
			if (tokens?.refreshToken) {
				await this.api.post("/auth/logout", {
					refreshToken: tokens.refreshToken,
				});
			}
		} catch (error) {
			console.error("Logout error:", error);
		} finally {
			this.clearTokens();
		}
	}

	public async logoutAll(): Promise<void> {
		try {
			await this.api.post("/auth/logout-all");
		} catch (error) {
			console.error("Logout all error:", error);
		} finally {
			this.clearTokens();
		}
	}

	public async getProfile(): Promise<User> {
		try {
			const response = await this.api.get("/auth/profile");

			if (response.data.success) {
				return response.data.data;
			} else {
				throw new Error(
					response.data.error?.message || "Failed to get profile",
				);
			}
		} catch (error: unknown) {
			if ((error as AxiosError<ApiResponse>).response?.data?.error?.message) {
				throw new Error(
					(error as AxiosError<ApiResponse>).response?.data.error?.message,
				);
			}
			throw new Error("Failed to get profile");
		}
	}

	public async updateProfile(name: string): Promise<User> {
		try {
			const response = await this.api.put("/auth/profile", { name });

			if (response.data.success) {
				return response.data.data;
			} else {
				throw new Error(
					response.data.error?.message || "Failed to update profile",
				);
			}
		} catch (error: unknown) {
			if ((error as AxiosError<ApiResponse>).response?.data?.error?.message) {
				throw new Error(
					(error as AxiosError<ApiResponse>).response?.data.error?.message,
				);
			}
			throw new Error("Failed to update profile");
		}
	}

	public async changePassword(
		currentPassword: string,
		newPassword: string,
	): Promise<void> {
		try {
			const response = await this.api.post("/auth/change-password", {
				currentPassword,
				newPassword,
			});

			if (!response.data.success) {
				throw new Error(
					response.data.error?.message || "Failed to change password",
				);
			}
		} catch (error: unknown) {
			if ((error as AxiosError<ApiResponse>).response?.data?.error?.message) {
				throw new Error(
					(error as AxiosError<ApiResponse>).response?.data.error?.message,
				);
			}
			throw new Error("Failed to change password");
		}
	}

	// Helper method to get stored tokens (for use in auth context)
	public getStoredTokensPublic(): AuthTokens | null {
		return this.getStoredTokens();
	}
}

export const authService = new AuthService();
